import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { setLanguage } from '../i18n';
import { onAuthEvent } from '../lib/auth-events';
import { fetchMe } from './auth-api';
import { useAuthStore } from './auth-store';

/**
 * Bootstraps the session exactly once on app start and keeps the store honest
 * afterwards.
 *
 * On mount it calls `/api/auth/me`. That request goes through the normal fetch
 * client, so a merely-expired 15-minute access token is repaired by the silent
 * `/auth/refresh` retry before we ever conclude "anonymous" — a user coming
 * back to an open tab after lunch must not be bounced to EMS.
 *
 * `session-expired` (emitted by the fetch client when the refresh could not
 * save a 401) drops identity and wipes the query cache: the next render is a
 * login redirect, and no other user's cached rows can survive into the next
 * session on a shared floor terminal.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const setSession = useAuthStore((s) => s.setSession);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const queryClient = useQueryClient();

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const me = await fetchMe(controller.signal);
        if (controller.signal.aborted) return;
        setSession(me);
        setLanguage(me.user.language);
      } catch {
        if (controller.signal.aborted) return;
        // 401 is the normal "not signed in yet" answer. Anything else (api
        // down, a 502 from nginx) lands here too: when identity cannot be
        // established, the login screen is the only safe place to be.
        setAnonymous();
      }
    })();

    return () => {
      controller.abort();
    };
  }, [setSession, setAnonymous]);

  useEffect(
    () =>
      onAuthEvent((event) => {
        if (event.type !== 'session-expired') return;
        setAnonymous();
        queryClient.clear();
      }),
    [setAnonymous, queryClient],
  );

  return children;
}
